import { useAnimatedList, Spinner } from '../components/feedback';
import { addonKindsOf } from '@mcpanel/core';
import { EditBoundary, useEditDirty } from '../components/EditBoundary';
import { OperationBoundary } from '../components/OperationBoundary';
import { LauncherSettingsCard } from './LauncherSettingsCard';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ChangeRequest, ServerView, SoftwareSpec } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { RequestReview } from '../components/RequestReview';
import { SoftwarePicker } from '../components/SoftwarePicker';
import { Dialog, Empty, ErrorAlert } from '../components/ui';
import { formatDateTime, softwareText } from '../format';
import { usePoll } from '../hooks/usePoll';

function sameSpec(a: SoftwareSpec, b: SoftwareSpec): boolean {
  return (
    a.family === b.family && a.mcVersion === b.mcVersion && a.build === b.build && a.loaderVersion === b.loaderVersion
  );
}

/** 作った変更リクエストをダイアログで確認・承認する */
export function ReviewDialog({ request, onClose }: { request: ChangeRequest; onClose: () => void }) {
  return (
    <Dialog
      title="変更内容の確認"
      wide
      onClose={onClose}
      actions={
        <button className="btn ghost" onClick={onClose}>
          閉じる（承認待ちのまま残す）
        </button>
      }
    >
      <RequestReview request={request} compact onDecided={onClose} />
    </Dialog>
  );
}

export function PendingForServer({ serverId }: { serverId: string }) {
  const pending = usePoll(() => api.requests({ status: 'pending', serverId }), 5000, [serverId]);
  if (!pending.data || pending.data.length === 0) return null;
  return (
    <div className="alert warn">
      このサーバーには承認待ちの依頼が {pending.data.length} 件あります。「承認」画面で確認してください。
    </div>
  );
}

function SoftwareTabContent({ server }: { server: ServerView }) {
  const { canOperate, notify } = useApp();
  const [target, setTarget] = useState<SoftwareSpec>(server.software);
  const [request, setRequest] = useState<ChangeRequest>();
  const [busy, setBusy] = useState(false);
  const changed = (server.state === 'error' || !sameSpec(target, server.software)) && target.mcVersion !== '';
  useEditDirty(!sameSpec(target, server.software));
  const [options, setOptions] = useState<import('@mcpanel/core').DowngradeOptions | undefined>({
    downgrade: false,
    snapshots: [],
  });
  const [optionsError, setOptionsError] = useState<string>();
  const modded = addonKindsOf(server.software.family).mods || addonKindsOf(target.family).mods;
  useEffect(() => {
    let active = true;
    if (target.mcVersion === server.software.mcVersion) {
      setOptions({ downgrade: false, snapshots: [] });
      setOptionsError(undefined);
      return;
    }
    setOptions(undefined);
    setOptionsError(undefined);
    if (!target.mcVersion) return;
    api
      .downgradeOptions(server.id, target)
      .then((value) => {
        if (active) setOptions(value);
      })
      .catch((error: unknown) => {
        if (active) setOptionsError(errorMessage(error));
      });
    return () => {
      active = false;
    };
  }, [server.id, server.revision, target.family, target.mcVersion]);
  const kind = target.family === server.software.family ? 'change_version' : 'change_software';

  const plan = async (snapshot?: string) => {
    setBusy(true);
    try {
      setRequest(
        await api.createRequest(
          snapshot ? 'restore_snapshot' : kind,
          snapshot ? { snapshot } : { software: target, ...(options?.downgrade ? { world: { mode: 'new' } } : {}) },
          server.id,
        ),
      );
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ maxWidth: 760 }}>
      {/* Mod のないサーバーは、拡張タブの「ランチャー」でクライアント Mod テンプレートと一緒に設定する */}
      {addonKindsOf(server.software.family).mods && <LauncherSettingsCard server={server} />}
      <PendingForServer serverId={server.id} />
      <section className="card card-body stack" style={{ gap: 6 }}>
        <h3>現在</h3>
        <div style={{ fontSize: 16, fontWeight: 600 }}>{softwareText(server.software)}</div>
      </section>
      <section className="card card-body stack">
        <h2>変更後</h2>
        <SoftwarePicker value={target} onChange={setTarget} currentSpec={server.software} />
        <div className="alert info small">
          ワールドは現世・ネザー・エンドを別々に保存しているので、Paper ⇄ Fabric
          のような系統の変更でも消えません。変更の前に自動でスナップショットを取ります。
          {modded && 'Mod サーバーはバージョンを下げることはできません（戻すときはスナップショットから復元します）。'}
        </div>
        {optionsError && <div className="alert danger">{optionsError}</div>}
        {options?.downgrade && !modded && (
          <div className="stack">
            <div className="alert warn">
              版を下げると、今のワールドはこの版では開けません。スナップショットから戻すと、ソフトと版もその時点に戻ります。
            </div>
            {options.snapshots.length === 0 && <p>戻せるスナップショットはありません。</p>}
            {options.snapshots.map((snapshot) => (
              <div key={snapshot.name} className="card card-body">
                <p>
                  {formatDateTime(snapshot.createdAt)} · {snapshot.software && softwareText(snapshot.software)} ·{' '}
                  {snapshot.note ?? 'メモなし'}
                </p>
                {canOperate && (
                  <button className="btn" disabled={busy} onClick={() => void plan(snapshot.name)}>
                    {busy && <Spinner />}
                    このスナップショットに戻す
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {canOperate && (
          <div className="row">
            <span className="spacer" />
            <button
              className="btn primary"
              disabled={!changed || busy || !options || (options.downgrade && modded)}
              onClick={() => void plan()}
            >
              {busy && <Spinner />}
              {options?.downgrade && !modded ? '新しいワールドで始める' : '変更内容を確認'}
            </button>
          </div>
        )}
      </section>
      {request && <ReviewDialog request={request} onClose={() => setRequest(undefined)} />}
    </div>
  );
}

function SnapshotsTabContent({ server }: { server: ServerView }) {
  const { canOperate, notify } = useApp();
  const snapshots = usePoll(() => api.snapshots(server.id), 15000, [server.id]);
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [request, setRequest] = useState<ChangeRequest>();

  const create = async () => {
    setCreating(false);
    try {
      await api.createSnapshot(server.id, note.trim() || undefined);
      setNote('');
      notify('スナップショットの作成を受け付けました');
      setTimeout(() => void snapshots.reload(), 1500);
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  const requestFor = async (kind: 'restore_snapshot' | 'delete_snapshot', snapshot: string) => {
    try {
      setRequest(await api.createRequest(kind, { snapshot }, server.id));
    } catch (error) {
      notify(errorMessage(error), true);
    }
  };

  const snapshotList = useAnimatedList(snapshots.data ?? [], (snapshot) => snapshot.name);
  const list = snapshotList.items;
  return (
    <div className="stack">
      <PendingForServer serverId={server.id} />
      <div className="card">
        <div className="card-header">
          <h2 data-search-id="snapshots">スナップショット</h2>
          {canOperate && (
            <button className="btn small" onClick={() => setCreating(true)}>
              今すぐ作成
            </button>
          )}
        </div>
        <ErrorAlert error={snapshots.error} />
        {list.length === 0 ? (
          <Empty>スナップショットはまだありません。</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>日時</th>
                  <th>当時のソフト</th>
                  <th>メモ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr
                    key={s.name}
                    ref={snapshotList.ref}
                    className={snapshotList.className(s)}
                    inert={!snapshots.data?.some((item) => item.name === s.name)}
                  >
                    <td className="small">
                      {formatDateTime(s.createdAt)}
                      <br />
                      <span className="faint">{s.kind === 'auto' ? '自動' : (s.createdBy ?? '手動')}</span>
                    </td>
                    <td className="small">{s.software ? softwareText(s.software) : '—'}</td>
                    <td className="small">
                      {s.note ?? ''}
                      <div className="faint mono">{s.name}</div>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {canOperate && (
                        <>
                          <button className="btn small" onClick={() => void requestFor('restore_snapshot', s.name)}>
                            復元
                          </button>{' '}
                          <button
                            className="btn ghost small danger"
                            onClick={() => void requestFor('delete_snapshot', s.name)}
                          >
                            削除
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="small faint" style={{ margin: 0 }}>
        復元すると、VM 全体（ワールドと設定）がその時点に戻ります。それ以降の変更は失われます。
      </p>
      {creating && (
        <Dialog
          title="スナップショットを作成"
          onClose={() => setCreating(false)}
          actions={
            <>
              <button className="btn" onClick={() => setCreating(false)}>
                キャンセル
              </button>
              <button className="btn primary" onClick={() => void create()}>
                作成
              </button>
            </>
          }
        >
          <p>稼働中でも作成できます（作成の一瞬だけワールドの保存を止めます）。</p>
          <div className="field">
            <label htmlFor="snap-note">メモ（任意）</label>
            <input
              id="snap-note"
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={80}
            />
          </div>
        </Dialog>
      )}
      {request && (
        <ReviewDialog
          request={request}
          onClose={() => {
            setRequest(undefined);
            void snapshots.reload();
          }}
        />
      )}
    </div>
  );
}

export function SoftwareTab({ server }: { server: ServerView }) {
  return (
    <OperationBoundary serverId={server.id}>
      <EditBoundary resource={{ target: `server:${server.id}`, screen: 'software' }} revision={String(server.revision)}>
        <SoftwareTabContent server={server} />
      </EditBoundary>
    </OperationBoundary>
  );
}
export function SnapshotsTab({ server }: { server: ServerView }) {
  return (
    <OperationBoundary serverId={server.id}>
      <SnapshotsTabContent server={server} />
    </OperationBoundary>
  );
}
